import React, { useCallback, useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  CircularProgress,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HistoryIcon from '@mui/icons-material/History';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonateDialogCloseIconSx,
  harmonateDrawerHeaderFlushSx,
} from '../../../theme/harmonateStyles';

const backendBase = () => (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '/');

function formatWhen(value) {
  if (!value) return '';
  // API timestamps are UTC. A value with no zone would be read as local time and show hours early.
  let raw = String(value);
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(raw) && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(raw)) {
    raw += 'Z';
  }
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function formatFieldName(field) {
  if (!field) return 'Field';
  return String(field)
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

function displayValue(value) {
  if (value === null || value === undefined || String(value).trim() === '') return '—';
  return String(value);
}

function parseChanges(value) {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== 'string') return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export async function recordWorkflowHistoricalActivity(entry) {
  const token = localStorage.getItem('token');
  const tenantId = localStorage.getItem('tenant_id');
  const userName = localStorage.getItem('username') || 'Unknown User';
  if (!token || !tenantId || !entry?.fileInstanceId) return false;

  const stepName = entry.stepName || 'Workflow';
  const action = entry.action;
  const response = await fetch(`${backendBase()}conductor/logs`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      tenantId,
      userName,
      dateTime: new Date().toISOString(),
      message: `${stepName}: ${action}`,
      action,
      functionName: 'Historical Activity',
      fileName: entry.fileName || null,
      workflowName: entry.workflowName || null,
      fileInstanceId: String(entry.fileInstanceId),
      stepName,
      fundName: entry.fundName || null,
      changes: JSON.stringify(entry.changes || []),
    }),
  });

  if (!response.ok) {
    throw new Error(`Historical activity was not saved (${response.status})`);
  }
  return true;
}

const HistoricalActivityDrawer = ({
  open,
  onClose,
  fileInstanceId,
  workflowName,
  fileName,
  fundName: fundNameProp = '',
  refreshKey = 0,
}) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const loadActivity = useCallback(async () => {
    const token = localStorage.getItem('token');
    const tenantId = localStorage.getItem('tenant_id');
    if (!token || !tenantId || !fileInstanceId) return;

    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        scope: 'tenant',
        tenantId,
        fileInstanceId: String(fileInstanceId),
        page: '0',
        size: '500',
      });
      const response = await fetch(
        `${backendBase()}conductor/logs?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) {
        throw new Error(`Could not load historical activity (${response.status})`);
      }
      const data = await response.json();
      const rows = Array.isArray(data?.content) ? data.content : [];
      setEntries(rows.map((row) => ({ ...row, changes: parseChanges(row.changes) })));
    } catch (err) {
      setError(err.message || 'Could not load historical activity');
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [fileInstanceId]);

  useEffect(() => {
    if (!open) return;
    loadActivity();
  }, [open, loadActivity, refreshKey]);

  const fundName = entries.find((entry) => entry.fundName)?.fundName || fundNameProp || '';

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 440 },
          maxWidth: '100vw',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box sx={harmonateDrawerHeaderFlushSx}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <HistoryIcon sx={{ color: ht.white, fontSize: 'small' }} />
          <Typography variant="subtitle1" fontWeight={600} sx={{ color: ht.white }}>
            Historical Activity
          </Typography>
        </Stack>
        <IconButton size="small" onClick={onClose} aria-label="Close historical activity" sx={harmonateDialogCloseIconSx}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${ht.line}`, bgcolor: ht.listBg }}>
        <Typography variant="caption" sx={{ color: ht.text, display: 'block' }}>
          Workflow: {workflowName || '—'}
        </Typography>
        <Typography variant="caption" sx={{ color: ht.text, display: 'block' }}>
          Fund: {fundName || '—'}
        </Typography>
        <Typography variant="caption" sx={{ color: ht.text, display: 'block' }} noWrap title={fileName || ''}>
          File: {fileName || '—'}
        </Typography>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} sx={{ color: ht.green }} />
          </Box>
        ) : entries.length === 0 && !error ? (
          <Typography variant="body2" color="text.secondary">
            No historical activity for this workflow yet. Edits and approvals will appear here.
          </Typography>
        ) : (
          entries.map((entry) => {
            const changes = Array.isArray(entry.changes) ? entry.changes : [];
            return (
              <Accordion
                key={entry.id}
                expanded={expandedId === entry.id}
                onChange={(_, isExpanded) => setExpandedId(isExpanded ? entry.id : null)}
                disableGutters
                elevation={0}
                sx={{
                  mb: 1,
                  border: `1px solid ${ht.line}`,
                  borderRadius: '8px !important',
                  '&:before': { display: 'none' },
                  overflow: 'hidden',
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ minWidth: 0, pr: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: ht.text }}>
                      {entry.stepName || 'Workflow'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: ht.text, display: 'block' }}>
                      {entry.userName || 'Unknown User'} · {entry.action}
                    </Typography>
                    <Typography variant="caption" sx={{ color: ht.text, display: 'block' }}>
                      {formatWhen(entry.dateTime)}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0, bgcolor: ht.listBg }}>
                  {changes.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      {entry.action === 'Approved' ? 'This workflow was approved.' : 'No field changes were recorded.'}
                    </Typography>
                  ) : (
                    <Stack spacing={1.25}>
                      {changes.map((change, index) => (
                        <Box key={`${entry.id}-${index}`}>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: ht.text }}>
                            {change.rowLabel || 'Record'} · {formatFieldName(change.field)}
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block', color: ht.text, whiteSpace: 'pre-wrap' }}>
                            Old: {displayValue(change.oldValue)}
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block', color: ht.text, whiteSpace: 'pre-wrap' }}>
                            New: {displayValue(change.newValue)}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  )}
                </AccordionDetails>
              </Accordion>
            );
          })
        )}
      </Box>
    </Drawer>
  );
};

export default HistoricalActivityDrawer;
